import Button from 'components/Button'
import {
  Card,
  CardContent,
  CardContentDescription,
  CardHeader,
  CardTitle,
} from 'components/Card'
import ProgressBar from 'components/ProgressBar'
import { observer } from 'mobx-react'
import React, { ReactNode } from 'react'
import JobViewModel from '../JobViewModel'
import { VoiceCallSteps } from './VoiceCallViewModel'
import * as Duration from 'dration'
import { match } from 'utils/pattern-match-util'
import TwilioVoiceCallSettingsModal from './TwilioVoiceCallSettingsModal'
import Icon from 'components/Icon'
import styles from './VoiceCallCard.sass'
import { formatToLongDateWithTimeZone } from 'utils/date-util'

interface StepContent {
  title: string
  description: string
}

interface Props {
  vm: JobViewModel
  completedState?: ReactNode
  completed?: boolean
  completeButton?: ReactNode
  initialContent?: StepContent
  completeOrPrepareContent?: StepContent
}

export default observer(function VoiceCallCard({
  vm,
  completed,
  completedState,
  initialContent,
  completeOrPrepareContent,
  completeButton,
}: Props) {
  return (
    <Card dataTest="current-task-new">
      <VoiceCallCardContent
        vm={vm}
        completed={completed}
        completedState={completedState}
        initialContent={initialContent}
        completeOrPrepareContent={completeOrPrepareContent}
        completeButton={completeButton}
      />
    </Card>
  )
})

export const VoiceCallCardContent = observer(function VoiceCallCardContent({
  vm,
  completed,
  completedState,
  initialContent,
  completeOrPrepareContent,
  completeButton,
}: Props) {
  const voiceCallVM = vm.voiceCallVM
  const step = voiceCallVM.callStep

  if (completed) {
    return <>{completedState}</>
  }

  return (
    <>
      {match(step, {
        INITIAL: () => <InitialContent vm={vm} title={initialContent?.title} />,
        PREPARING: () => <PreparingContent vm={vm} />,
        DIALING: () => <DialingContent />,
        IN_PROGRESS: () => <InProgressContent vm={vm} />,
        COMPLETE_OR_PREPARE: () => (
          <CompleteOrPrepareContent
            vm={vm}
            title={completeOrPrepareContent?.title}
            description={completeOrPrepareContent?.description}
            completeButton={completeButton}
          />
        ),
      })}
      {voiceCallVM.voiceCallSettingsModalVM.showing ? (
        <TwilioVoiceCallSettingsModal
          vm={voiceCallVM.voiceCallSettingsModalVM}
        />
      ) : null}
    </>
  )
})
interface VoiceCallCardContent {
  vm: JobViewModel
  title?: string
  description?: string
}
const InitialContent = observer(function InitialContent({
  vm,
  title,
}: VoiceCallCardContent) {
  const stepDetails = VoiceCallSteps.INITIAL
  const consultation = vm.job.consultation
  return (
    <>
      <CardHeader>
        <CardTitle dataTest="current-task-title">
          {title || 'Prepare for the call'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ProgressBar
          bars={[
            { progress: stepDetails.completed },
            { progress: stepDetails.pending, style: 'striped' },
          ]}
        />
        <br />
        {!consultation || consultation?.skipScheduling ? (
          <CardContentDescription>
            <p>
              <span className={styles.important}>
                {'Schedule a call time with the client. '}
              </span>
              To start the consultation, prepare your audio settings.
            </p>
          </CardContentDescription>
        ) : (
          <CardContentDescription>
            <p>
              Call scheduled with client on
              <span className={styles.important}>
                {` ${formatToLongDateWithTimeZone(
                  consultation?.bookedTime?.startTime
                )}. `}
              </span>
              To start the consultation, prepare your audio settings.
            </p>
          </CardContentDescription>
        )}
      </CardContent>
      <CardContent>
        <Button
          type="primary"
          size="wide-slim"
          disabled={vm.voiceCallVM.prepareCall.pending}
          loading={vm.voiceCallVM.prepareCall.pending}
          onClick={() => vm.voiceCallVM.prepareCall()}
          dataTest="prepare-call-button"
        >
          Prepare Call
        </Button>
      </CardContent>
    </>
  )
})

const PreparingContent = observer(function PreparingContent({
  vm,
}: VoiceCallCardContent) {
  const stepDetails = VoiceCallSteps.PREPARING

  return (
    <>
      <CardHeader>
        <CardTitle dataTest="current-task-title">
          Set up your mic and speakers
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ProgressBar
          bars={[
            { progress: stepDetails.completed },
            { progress: stepDetails.pending, style: 'striped' },
          ]}
        />
        <br />
        <CardContentDescription>
          Review your audio settings, and start the call when ready, or{' '}
          <Button
            onClick={vm.voiceCallVM.cancelPreparation}
            type="text"
            size="baseline"
          >
            go back
          </Button>{' '}
          to the previous step.
        </CardContentDescription>
      </CardContent>
      <CardContent compactBottomPadding>
        <Button
          type="secondary"
          size="wide-slim"
          disabled={vm.voiceCallVM.startCall.pending}
          loading={vm.voiceCallVM.startCall.pending}
          onClick={vm.voiceCallVM.voiceCallSettingsModalVM.show}
        >
          Audio Settings
        </Button>
      </CardContent>
      <CardContent>
        <Button
          type="primary"
          size="wide-slim"
          disabled={vm.voiceCallVM.startCall.pending}
          loading={vm.voiceCallVM.startCall.pending}
          onClick={() => vm.voiceCallVM.startCall()}
        >
          Start Call
        </Button>
      </CardContent>
    </>
  )
})

interface CompleteOrPrepareProps extends VoiceCallCardContent {
  completeButton?: ReactNode
}
const CompleteOrPrepareContent = observer(function CompleteOrPrepareContent({
  vm,
  title,
  description,
  completeButton,
}: CompleteOrPrepareProps) {
  const stepDetails = VoiceCallSteps.COMPLETE_OR_PREPARE

  return (
    <>
      <CardHeader>
        <CardTitle dataTest="current-task-title">
          {title || 'Prepare for the call'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ProgressBar
          bars={[
            { progress: stepDetails.completed },
            { progress: stepDetails.pending, style: 'striped' },
          ]}
        />
        <br />
        <CardContentDescription>
          {description || 'Start another call with the client'}
        </CardContentDescription>
      </CardContent>
      <CardContent compactBottomPadding={completeButton !== undefined}>
        <Button
          type="secondary"
          size="wide-slim"
          disabled={vm.voiceCallVM.prepareCall.pending}
          loading={vm.voiceCallVM.prepareCall.pending}
          onClick={() => vm.voiceCallVM.prepareCall()}
        >
          Prepare Another Call
        </Button>
      </CardContent>
      {completeButton}
    </>
  )
})

const DialingContent = observer(function InProgressContent() {
  const stepDetails = VoiceCallSteps.DIALING

  return (
    <>
      <CardHeader>
        <CardTitle dataTest="current-task-title">Dialing...</CardTitle>
      </CardHeader>
      <CardContent>
        <ProgressBar
          bars={[
            { progress: stepDetails.completed },
            { progress: stepDetails.pending, style: 'striped' },
          ]}
        />
        <br />
        <CardContentDescription>
          Calling the client. Please hold.
        </CardContentDescription>
      </CardContent>
    </>
  )
})

const InProgressContent = observer(function InProgressContent({
  vm,
}: VoiceCallCardContent) {
  const stepDetails = VoiceCallSteps.IN_PROGRESS

  const consultationLength = vm.job.consultation?.requestedLength

  return (
    <>
      <CardHeader fullWidth>
        <CardTitle fullWidth dataTest="current-task-title">
          <div className={styles.titleContainer}>
            <div className={styles.title}>Call in progress</div>
            <div className={styles.callDuration}>
              {Duration.format(vm.voiceCallVM.duration)}
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ProgressBar
          bars={[
            { progress: stepDetails.completed },
            { progress: stepDetails.pending, style: 'striped' },
          ]}
        />
        <br />
        <CardContentDescription>
          The {consultationLength ? `${consultationLength} minute` : ''}{' '}
          consultation call is currently in progress.
        </CardContentDescription>
      </CardContent>
      <CardContent compactBottomPadding>
        <div className={styles.row}>
          <Button
            type="secondary"
            size="wide-slim"
            disabled={vm.voiceCallVM.prepareCall.pending}
            loading={vm.voiceCallVM.prepareCall.pending}
            onClick={vm.voiceCallVM.voiceCallSettingsModalVM.show}
          >
            Audio Settings
          </Button>
          <Button
            type="secondary"
            size="wide-slim"
            className={styles.muteButton}
            disabled={vm.voiceCallVM.prepareCall.pending}
            loading={vm.voiceCallVM.prepareCall.pending}
            onClick={() => vm.voiceCallVM.toggleMute()}
          >
            <Icon
              size={23}
              name={
                vm.voiceCallVM.twilioCall?.isMuted()
                  ? 'call-mic-off'
                  : 'call-mic-on'
              }
            />
          </Button>
        </div>
      </CardContent>
      <CardContent>
        <Button
          type="danger"
          size="wide-slim"
          dataTest="complete-job-new"
          className={styles.completeButton}
          disabled={vm.voiceCallVM.completeCall.pending}
          loading={vm.voiceCallVM.completeCall.pending}
          onClick={vm.voiceCallVM.completeCall}
        >
          End Call
        </Button>
      </CardContent>
    </>
  )
})
